import { apiClient } from './client';

// Local type definitions (mirrors @gps-hunt/shared)
type ChallengeType = 'riddle' | 'hex_code' | 'numerical';
interface PinChallenge {
  type: ChallengeType;
  question: string;
  answer: string;
}

// Response types for pin endpoints
export interface CreatePinResponse {
  pinId: string;
  huntId: string;
  latitude: number;
  longitude: number;
  accuracy: number;
  itemCount: number;
  sequenceOrder: number;
  createdAt: string;
  challenge?: PinChallenge;
}

export interface GetPinsResponse {
  pins: Array<{
    id: string;
    latitude: number;
    longitude: number;
    accuracy: number;
    itemCount: number;
    hintPhotoUrl?: string;
    sequenceOrder: number;
    challenge?: { type: string; question: string; answer: string };
  }>;
}

export interface HunterPinsResponse {
  currentPinIndex: number;
  totalPins: number;
  currentPin: {
    id: string;
    latitude: number;
    longitude: number;
    itemCount: number;
    hintPhotoUrl: string | null;
  } | null;
}

export interface PhotoUploadUrlResponse {
  uploadUrl: string;
  blobName: string;
  expiresAt: string;
}

export interface ConfirmPhotoResponse {
  hintPhotoUrl: string;
}

export interface MarkReadyResponse {
  isReady: boolean;
  allHidersReady: boolean;
  huntStatus: string;
}

export interface MarkNotReadyResponse {
  isReady: boolean;
  huntStatus: string;
}

/**
 * Pin API service
 */
export const pinService = {
  /**
   * Create a new pin
   */
  async createPin(
    huntId: string,
    data: {
      latitude: number;
      longitude: number;
      accuracy: number;
      itemCount: number;
      challenge?: PinChallenge;
    }
  ): Promise<CreatePinResponse> {
    const response = await apiClient.post<CreatePinResponse>(
      `/hunts/${huntId}/pins`,
      data
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to create pin');
    }

    return response.data!;
  },

  /**
   * Get all pins for a hunt (hider view)
   */
  async getPins(huntId: string): Promise<GetPinsResponse> {
    const response = await apiClient.get<GetPinsResponse>(
      `/hunts/${huntId}/pins`
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to get pins');
    }

    return response.data!;
  },

  /**
   * Delete a pin
   */
  async deletePin(huntId: string, pinId: string): Promise<void> {
    const response = await apiClient.delete(`/hunts/${huntId}/pins/${pinId}`);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete pin');
    }
  },

  /**
   * Update a pin
   */
  async updatePin(
    huntId: string,
    pinId: string,
    data: {
      itemCount: number;
      challenge?: PinChallenge | null;
    }
  ): Promise<{ pinId: string; itemCount: number; hasChallenge: boolean; challengeType?: string }> {
    const response = await apiClient.put<{ pinId: string; itemCount: number; hasChallenge: boolean; challengeType?: string }>(
      `/hunts/${huntId}/pins/${pinId}`,
      data
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update pin');
    }

    return response.data!;
  },

  /**
   * Reorder pins
   */
  async reorderPins(huntId: string, pinOrder: string[]): Promise<void> {
    const response = await apiClient.put(`/hunts/${huntId}/pins/reorder`, {
      pinOrder,
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to reorder pins');
    }
  },

  /**
   * Get photo upload URL
   */
  async getPhotoUploadUrl(
    huntId: string,
    pinId: string
  ): Promise<PhotoUploadUrlResponse> {
    const response = await apiClient.post<PhotoUploadUrlResponse>(
      `/hunts/${huntId}/pins/${pinId}/photo/upload-url`
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to get upload URL');
    }

    return response.data!;
  },

  /**
   * Upload photo to blob storage
   */
  async uploadPhoto(uploadUrl: string, file: Blob): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': file.type || 'image/jpeg',
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error('Failed to upload photo');
    }
  },

  /**
   * Confirm photo upload
   */
  async confirmPhotoUpload(
    huntId: string,
    pinId: string,
    blobName: string
  ): Promise<ConfirmPhotoResponse> {
    const response = await apiClient.post<ConfirmPhotoResponse>(
      `/hunts/${huntId}/pins/${pinId}/photo/confirm`,
      { blobName }
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to confirm upload');
    }

    return response.data!;
  },

  /**
   * Mark participant as ready
   */
  async markReady(huntId: string): Promise<MarkReadyResponse> {
    const response = await apiClient.post<MarkReadyResponse>(
      `/hunts/${huntId}/ready`
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to mark ready');
    }

    return response.data!;
  },

  /**
   * Mark hider as not ready (go back to make changes)
   */
  async markNotReady(huntId: string): Promise<MarkNotReadyResponse> {
    const response = await apiClient.post<MarkNotReadyResponse>(
      `/hunts/${huntId}/not-ready`
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to go back');
    }

    return response.data!;
  },

  // Challenge methods

  /**
   * Encounter a challenge when trying to complete a pin
   * This triggers the surprise reveal - first hunter becomes captain
   */
  async encounterChallenge(
    huntId: string,
    pinId: string,
    userId: string,
    displayName: string
  ): Promise<{
    success: boolean;
    hasChallenge: boolean;
    isCaptain?: boolean;
    alreadySolved?: boolean;
    state?: ChallengeState;
    challenge?: { type: string; question: string };
  }> {
    const response = await apiClient.post<{
      success: boolean;
      hasChallenge: boolean;
      isCaptain?: boolean;
      alreadySolved?: boolean;
      state: ChallengeState;
      challenge: { type: string; question: string };
    }>(`/hunts/${huntId}/pins/${pinId}/challenge/encounter`, { userId, displayName });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to encounter challenge');
    }

    return response.data!;
  },
  
  /**
   * Lock a challenge to start attempting it
   */
  async lockChallenge(
    huntId: string,
    pinId: string,
    userId: string,
    displayName: string
  ): Promise<{ success: boolean; state?: ChallengeState; challenge?: { type: string; question: string }; message?: string }> {
    const response = await apiClient.post<{ success: boolean; state: ChallengeState; challenge: { type: string; question: string }; message?: string }>(
      `/hunts/${huntId}/pins/${pinId}/challenge/lock`,
      { userId, displayName }
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to lock challenge');
    }

    return response.data!;
  },

  /**
   * Unlock a challenge (release without solving)
   */
  async unlockChallenge(
    huntId: string,
    pinId: string,
    userId: string
  ): Promise<{ success: boolean; state?: ChallengeState }> {
    const response = await apiClient.post<{ success: boolean; state: ChallengeState }>(
      `/hunts/${huntId}/pins/${pinId}/challenge/unlock`,
      { userId }
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to unlock challenge');
    }

    return response.data!;
  },

  /**
   * Submit challenge answer
   */
  async submitChallengeAnswer(
    huntId: string,
    pinId: string,
    userId: string,
    displayName: string,
    answer: string
  ): Promise<{ correct: boolean; message: string; state?: ChallengeState; alreadySolved?: boolean }> {
    const response = await apiClient.post<{ correct: boolean; message: string; state?: ChallengeState; alreadySolved?: boolean }>(
      `/hunts/${huntId}/pins/${pinId}/challenge/submit`,
      { userId, displayName, answer }
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to submit answer');
    }

    return response.data!;
  },

  /**
   * Get challenge state for a pin
   */
  async getChallengeState(
    huntId: string,
    pinId: string
  ): Promise<{ hasChallenge: boolean; challengeType?: string; question?: string; state?: ChallengeState }> {
    const response = await apiClient.get<{ hasChallenge: boolean; challengeType?: string; question?: string; state?: ChallengeState }>(
      `/hunts/${huntId}/pins/${pinId}/challenge/state`
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to get challenge state');
    }

    return response.data!;
  },

  /**
   * Decrement item found count (hider referee action)
   */
  async decrementItemFound(
    huntId: string,
    pinId: string
  ): Promise<{ totalFoundAtPin: number; pinItemCount: number }> {
    const response = await apiClient.post<{ totalFoundAtPin: number; pinItemCount: number }>(
      `/hunts/${huntId}/pins/${pinId}/items/decrement`
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to decrement item count');
    }

    return response.data!;
  },
};

// ChallengeState type for client
export interface ChallengeState {
  pinId: string;
  isActive: boolean;
  lockedByUserId?: string;
  lockedByDisplayName?: string;
  lockedAt?: string;
  isSolved: boolean;
  solvedByUserId?: string;
  solvedByDisplayName?: string;
  solvedAt?: string;
}

export default pinService;
